"use client"

import { useState } from "react"
import { User } from "@/schema"
import {
  Button,
  Command,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ui/shad"
import { Plus } from "lucide-react"

import { useFetchUsers } from "@/hooks/api/users"
import { Collaborators } from "@/components/users/collaborators"

interface CollaboratorSelectorProps {
  collaboratorIds: string[]
  onAdd: (user: User) => void
  onRemove: (userId: string) => void
  className?: string
  triggerButton?: React.ReactNode
}

export function CollaboratorSelector({
  collaboratorIds,
  onAdd,
  onRemove,
  className = "",
  triggerButton,
}: CollaboratorSelectorProps) {
  const { data: users } = useFetchUsers()
  const [showCollaboratorSelector, setShowCollaboratorSelector] = useState(false)

  const handleAddCollaborator = (user: User) => {
    onAdd(user)
    setShowCollaboratorSelector(false)
  }

  // Filter out users that are already collaborators
  const availableUsers = users?.users?.filter((user) => !collaboratorIds.includes(user.id)) || []

  return (
    <div className={`group flex items-center gap-2 ${className} min-h-8`}>
      {collaboratorIds && collaboratorIds.length > 0 && (
        <div className="flex items-center">
          <Collaborators userIds={collaboratorIds} onRemove={onRemove} />
        </div>
      )}
      <Popover open={showCollaboratorSelector} onOpenChange={setShowCollaboratorSelector}>
        <PopoverTrigger asChild>
          {triggerButton ? (
            <div onClick={() => setShowCollaboratorSelector(true)}>{triggerButton}</div>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="bg-secondary h-8 w-8 rounded-full"
              onClick={() => setShowCollaboratorSelector(true)}
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-[250px] p-0" side="bottom" align="end">
          <Command>
            <CommandInput placeholder="Search users..." />
            <CommandList>
              <div className="p-1">
                {availableUsers.length > 0 ? (
                  availableUsers.map((user) => (
                    <CommandItem key={user.id} onSelect={() => handleAddCollaborator(user)}>
                      {user.full_name}
                    </CommandItem>
                  ))
                ) : (
                  <div className="text-muted-foreground p-2 text-sm">No more collaborators to add</div>
                )}
              </div>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}

export default CollaboratorSelector
